"use client";

import Link from "next/link";

import { APP_NAME } from "@calcom/lib/constants";
import { getOrgOrTeamAvatar } from "@calcom/lib/defaultAvatarImage";
import { useLocale } from "@calcom/lib/hooks/useLocale";
import { Avatar } from "@calcom/ui/components/avatar";
import { Badge } from "@calcom/ui/components/badge";

export type TeamPublicViewData = {
  name: string;
  slug: string;
  logoUrl: string | null;
  bio: string | null;
  members: {
    name: string | null;
    username: string | null;
    avatarUrl: string | null;
  }[];
  eventTypes: {
    slug: string;
    title: string;
    description: string | null;
    length: number;
    schedulingType: string | null;
  }[];
};

export default function TeamPublicView({ team }: { team: TeamPublicViewData }) {
  const { t } = useLocale();
  return (
    <div className="mx-auto w-full max-w-2xl px-4 py-8">
      <div className="mb-8 flex flex-col items-center text-center">
        <Avatar
          alt={team.name}
          imageSrc={getOrgOrTeamAvatar(team) ?? undefined}
          size="xl"
          className="mb-4"
        />
        <h1 className="font-cal mb-1 text-2xl font-semibold">{team.name}</h1>
        {team.bio && <p className="text-subtle text-sm">{team.bio}</p>}
      </div>

      {team.eventTypes.length > 0 && (
        <section className="mb-8">
          <h2 className="text-emphasis mb-3 text-sm font-medium uppercase tracking-wide">
            {t("team_event_types")}
          </h2>
          <ul className="border-subtle divide-subtle overflow-hidden rounded-md border">
            {team.eventTypes.map((eventType) => (
              <li key={eventType.slug} className="border-subtle [&+li]:border-t">
                <Link
                  href={`/team/${team.slug}/${eventType.slug}`}
                  className="bg-default hover:bg-subtle flex items-center justify-between px-4 py-3 transition-colors">
                  <span className="min-w-0">
                    <span className="text-emphasis block truncate font-medium">{eventType.title}</span>
                    {eventType.description && (
                      <span className="text-subtle block truncate text-sm">{eventType.description}</span>
                    )}
                  </span>
                  <span className="text-subtle ml-4 flex shrink-0 items-center gap-2 text-sm">
                    {eventType.schedulingType && (
                      <Badge variant="gray">
                        {eventType.schedulingType === "ROUND_ROBIN"
                          ? t("round_robin")
                          : t("collective")}
                      </Badge>
                    )}
                    {t("x_minutes", { count: eventType.length })}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {team.members.length > 0 && (
        <section>
          <h2 className="text-emphasis mb-3 text-sm font-medium uppercase tracking-wide">
            {t("team_members")}
          </h2>
          <ul className="border-subtle divide-subtle overflow-hidden rounded-md border">
            {team.members.map((member) => (
              <li key={member.username ?? member.name ?? ""} className="border-subtle [&+li]:border-t">
                {member.username ? (
                  <Link
                    href={`/${member.username}`}
                    className="bg-default hover:bg-subtle flex items-center gap-3 px-4 py-3 transition-colors">
                    <Avatar alt={member.name ?? ""} imageSrc={member.avatarUrl ?? undefined} size="sm" />
                    <span className="min-w-0">
                      <span className="text-emphasis block truncate font-medium">{member.name}</span>
                      <span className="text-subtle block truncate text-sm">
                        {member.username} · {APP_NAME}
                      </span>
                    </span>
                  </Link>
                ) : (
                  <div className="bg-default flex items-center gap-3 px-4 py-3">
                    <Avatar alt={member.name ?? ""} imageSrc={member.avatarUrl ?? undefined} size="sm" />
                    <span className="text-emphasis font-medium">{member.name}</span>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
